/**
 * 워크스페이스 선택/필터 헬퍼 — 프로젝트 목록(?ws= 파라미터)·사이드바·이동 대화상자·실행 목록 묶음이 공유하는 선택 모델.
 *
 * 선택값은 문자열 하나: 'all'(전체) | 워크스페이스 id(uuid). 예약어는 'all' 하나다.
 * 기본 워크스페이스는 사용자마다 하나 있는 **행**(`is_default`, bff 0017)이라 다른 행과 같이 id 로 고른다 — «무소속» 이라는
 * 상태가 없으므로 그것을 가리키던 'default' 예약어도 없다(옛 주소·저장값의 'default' 는 무효 선택으로 떨어져 기본 행으로 정정된다).
 * 기본 행의 판별과 자리(목록 선두)는 여기 한 곳이 정한다.
 */
import type { Workspace } from '@/types/project';

export const WORKSPACE_ALL = 'all';
/** 적재된 프로젝트 목록에 없는(삭제된) 프로젝트의 실행 버킷 — 실행 목록 전용 스코프 */
export const WORKSPACE_DELETED = 'deleted';

export function isWorkspaceId(selection: string): boolean {
  return selection !== WORKSPACE_ALL;
}

/** 선택에 해당하는 프로젝트만 남긴다 — 소속 id 일치 */
export function filterProjectsByWorkspace<T extends { workspace_id: string }>(
  projects: T[],
  selection: string,
): T[] {
  if (selection === WORKSPACE_ALL) return projects;
  return projects.filter((p) => p.workspace_id === selection);
}

/** 워크스페이스별 프로젝트 수 — 사이드바 카운트 뱃지용. 키는 소속 id */
export function countProjectsByWorkspace(
  projects: Array<{ workspace_id: string }>,
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const p of projects) counts[p.workspace_id] = (counts[p.workspace_id] ?? 0) + 1;
  return counts;
}

type DefaultFlagged = Pick<Workspace, 'is_default'>;

/** 사용자의 기본 워크스페이스 행 — 목록을 받기 전·조회 실패면 undefined(화면은 선택을 보류한다) */
export function defaultWorkspaceOf<W extends DefaultFlagged>(workspaces: readonly W[]): W | undefined {
  return workspaces.find((w) => w.is_default);
}

export type WorkspaceSort = 'created' | 'name';

/** 화면 순서 — 기본 행이 먼저, 나머지는 서버 순서(created_at 오름차순) 그대로 또는 이름순 */
export function orderWorkspaces<W extends DefaultFlagged & Pick<Workspace, 'name'>>(
  workspaces: readonly W[],
  sort: WorkspaceSort,
): W[] {
  const rest = workspaces.filter((w) => !w.is_default);
  const ordered = sort === 'name' ? [...rest].sort((a, b) => a.name.localeCompare(b.name, 'ko')) : rest;
  const def = defaultWorkspaceOf(workspaces);
  return def ? [def, ...ordered] : ordered;
}
